import React, { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import { currentWeatherConditionsPageTestIDs, hourlyWeatherPageTestIDs, Theme } from '@constants';
import { useContextNodeHandle, useGetUserLocation } from '@hooks';
import { navigationService } from '@services';
import { dayDateTimeState } from '@store';

import { getWeatherPhrase } from '../../../helpers/getWeatherPhrase';
import { isAndroid } from '../../../helpers/Platform';
import { displayString, isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { TWCIcon } from '../../primitives/TWCIcon/TWCIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'currentWeatherConditionsPage.currentWeatherWidget.';
const testIDPath = currentWeatherConditionsPageTestIDs.currentWeatherWidget;

const CurrentWeatherWidgetLeft = memo(({ error, iconCodeExtend, temperature, temperatureFeelsLike, weatherPhrase }) => {
  const { t } = useTranslation();

  return (
    <View style={styles.currentWeatherWidgetLeftWrapper}>
      <View style={styles.currentWeatherWidgetLeftTopRow}>
        <TWCIcon code={iconCodeExtend} height={108} type={'current'} width={108} />
        <TWCText style={{ paddingLeft: Theme.px(40) }} testID={testIDPath.temperatureText} variant={'largeText'}>
          {isDisplayable(temperature, error) ? `${temperature}°` : missingValue}
        </TWCText>
      </View>
      <View>
        <TWCText testID={testIDPath.wxPhraseText} variant={'regularText36'}>
          {displayString(weatherPhrase, error)}
        </TWCText>
        <View style={styles.currentWeatherWidgetFlexRowWrapper}>
          <TWCText testID={testIDPath.temperatureFeelsLikeLabelText} variant={'regularText30Transparent'}>
            {t(`${tPath}temperatureFeelsLikeLabel`)}
          </TWCText>
          <TWCText testID={testIDPath.temperatureFeelsLikeText} variant={'regularText30'}>
            {isDisplayable(temperatureFeelsLike, error) ? ` ${temperatureFeelsLike}°` : ` ${missingValue}`}
          </TWCText>
        </View>
      </View>
    </View>
  );
});

const CurrentWeatherWidgetRightItem = ({
  error,
  firstText,
  firstTextTestID,
  secondText,
  secondTextTestID,
  title,
  titleTestID,
}) => {
  const { t } = useTranslation();

  return (
    <View style={styles.currentWeatherWidgetRightItemWrapper}>
      <TWCText testID={titleTestID} variant={'regularText30Transparent'}>
        {t(`${tPath}${title}`)}
      </TWCText>
      <View style={styles.currentWeatherWidgetRightItemContent}>
        <TWCText testID={firstTextTestID} variant={'regularText54'}>
          {displayString(firstText, error)}
        </TWCText>
        {secondText && !error ? (
          <TWCText testID={secondTextTestID} variant={'regularText42'}>
            {t(`${tPath}${secondText}`)}
          </TWCText>
        ) : null}
      </View>
    </View>
  );
};

const CurrentWeatherWidgetRight = memo(
  ({
    error,
    precip24Hour,
    pressureAltimeter,
    relativeHumidity,
    temperatureDewPoint,
    uvIndex,
    visibility,
    windDirectionCardinal,
    windGust,
    windSpeed,
  }) => {
    const { t } = useTranslation();

    return (
      <View style={styles.currentWeatherWidgetRightWrapper}>
        <View style={styles.currentWeatherWidgetRightTopRow}>
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={`${windDirectionCardinal} ${windSpeed}`}
            firstTextTestID={testIDPath.windText}
            secondText={'windUnits'}
            secondTextTestID={testIDPath.windUnitsText}
            title={'windDirectionCardinalWindSpeedLabel'}
            titleTestID={testIDPath.windTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={windGust ? t(`${tPath}windGustText`, { windGust }) : t(`${tPath}windGustNone`)}
            firstTextTestID={testIDPath.windGustText}
            secondText={windGust && 'windUnits'}
            secondTextTestID={testIDPath.windUnitsText}
            title={'windGustLabel'}
            titleTestID={testIDPath.windGustTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(precip24Hour, error) ? `${precip24Hour}”` : missingValue}
            firstTextTestID={testIDPath.precip24HourText}
            title={'precip24HourLabel'}
            titleTestID={testIDPath.precip24HourTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(relativeHumidity, error) ? `${relativeHumidity}%` : missingValue}
            firstTextTestID={testIDPath.relativeHumidityText}
            title={'relativeHumidityLabel'}
            titleTestID={testIDPath.relativeHumidityTitleText}
          />
        </View>
        <View style={styles.currentWeatherWidgetFlexRowWrapper}>
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(temperatureDewPoint, error) ? `${temperatureDewPoint}°` : missingValue}
            firstTextTestID={testIDPath.temperatureDewPointText}
            title={'temperatureDewPointLabel'}
            titleTestID={testIDPath.temperatureDewPointTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={isDisplayable(pressureAltimeter, error) ? `${pressureAltimeter}”` : missingValue}
            firstTextTestID={testIDPath.pressureAltimeterText}
            title={'pressureAltimeterLabel'}
            titleTestID={testIDPath.pressureAltimeterTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={displayString(uvIndex, error)}
            firstTextTestID={testIDPath.uvIndexText}
            title={'uvIndexLabel'}
            titleTestID={testIDPath.uvIndexTitleText}
          />
          <CurrentWeatherWidgetRightItem
            error={error}
            firstText={displayString(visibility, error)}
            firstTextTestID={testIDPath.visibilityText}
            secondText={'visibilityUnits'}
            secondTextTestID={testIDPath.visibilityUnitsText}
            title={'visibilityLabel'}
            titleTestID={testIDPath.visibilityTitleText}
          />
        </View>
      </View>
    );
  },
);

/**
 * Note that this component should never actually need the loading state, as the hook will have already been fetched when the user accesses this page
 * Leaving it in for completion's sake though idk it looks nice in Storybook to have a loading state
 */
export const CurrentWeatherWidget = forwardRef(({ data, error, isLoading, nextFocusDown, nextFocusUp }, reference) => {
  const { t } = useTranslation();
  const { name } = useGetUserLocation();
  const { timeZone } = useRecoilValue(dayDateTimeState);

  const date = TimeUtils.getZonedDate(new Date(), timeZone);
  const time = TimeUtils.getZonedTime(new Date(), timeZone, '12h');

  const thisHandle = useContextNodeHandle(currentWeatherConditionsPageTestIDs.currentWeatherConditionsWidget);
  const noFocus = isAndroid ? thisHandle : null;

  const {
    iconCodeExtend,
    precip24Hour,
    pressureAltimeter,
    relativeHumidity,
    temperature,
    temperatureDewPoint,
    temperatureFeelsLike,
    uvIndex,
    visibility,
    windDirectionCardinal,
    windGust,
    windSpeed,
  } = data ? data : {};

  const weatherPhrase = getWeatherPhrase({ iconCodeExtend });

  return (
    <>
      <View style={styles.currentWeatherWidgetBackground} />
      <TWCTile
        accessibilityHint={t(`${tPath}accessibilityHint`)}
        accessibilityLabel={
          error
            ? t(`${tPath}errorAccessibilityLabel`)
            : isAndroid
            ? t(`${tPath}accessibilityLabel`, {
                precip24Hour,
                pressureAltimeter,
                relativeHumidity,
                temperature,
                temperatureDewPoint,
                temperatureFeelsLike,
                uvIndex,
                visibility,
                weatherPhrase,
                windDirectionCardinal,
                windGust: windGust ? `${windGust}mph` : 'no',
                windSpeed,
              })
            : t(`${tPath}nonAndroidAccessibilityLabel`, {
                name,
                date,
                time,
                precip24Hour,
                pressureAltimeter,
                relativeHumidity,
                temperature,
                temperatureDewPoint,
                temperatureFeelsLike,
                uvIndex,
                visibility,
                weatherPhrase,
                windDirectionCardinal,
                windGust: windGust ? `${windGust}mph` : 'no',
                windSpeed,
              })
        }
        height={480}
        nextFocusDown={nextFocusDown ?? noFocus}
        nextFocusLeft={noFocus}
        nextFocusRight={noFocus}
        nextFocusUp={nextFocusUp ?? noFocus}
        ref={reference}
        testID={currentWeatherConditionsPageTestIDs.currentWeatherConditionsWidget}
        useLinearGradient={false}
        width={1740}
        onPress={() => navigationService.navigate(hourlyWeatherPageTestIDs.hourlyWeatherPage)}
      >
        {isLoading ? (
          <View style={styles.currentWeatherWidgetLoadingWrapper}>
            <ActivityIndicator color={Theme.loading} size={'large'} />
          </View>
        ) : (
          <View style={styles.currentWeatherWidgetContent}>
            <CurrentWeatherWidgetLeft
              error={error}
              iconCodeExtend={iconCodeExtend}
              temperature={temperature}
              temperatureFeelsLike={temperatureFeelsLike}
              weatherPhrase={weatherPhrase}
            />
            <CurrentWeatherWidgetRight
              error={error}
              precip24Hour={precip24Hour}
              pressureAltimeter={pressureAltimeter}
              relativeHumidity={relativeHumidity}
              temperatureDewPoint={temperatureDewPoint}
              uvIndex={uvIndex}
              visibility={visibility}
              windDirectionCardinal={windDirectionCardinal}
              windGust={windGust}
              windSpeed={windSpeed}
            />
          </View>
        )}
      </TWCTile>
    </>
  );
});

CurrentWeatherWidget.propTypes = {
  data: PropTypes.object,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

CurrentWeatherWidget.defaultProps = {
  data: null,
  error: null,
  isLoading: false,
  nextFocusDown: null,
  nextFocusUp: null,
};

const styles = StyleSheet.create({
  currentWeatherWidgetBackground: {
    backgroundColor: Theme.whisperWhite,
    borderRadius: Theme.tileBorderRadius,
    bottom: Theme.px(6),
    height: Theme.px(480),
    opacity: 0.05,
    position: 'absolute',
    width: Theme.px(1740),
  },
  currentWeatherWidgetContent: {
    flexDirection: 'row',
    paddingBottom: Theme.px(122),
    paddingLeft: Theme.px(102),
  },
  currentWeatherWidgetLeftTopRow: {
    alignItems: 'center',
    flexDirection: 'row',
    paddingBottom: Theme.px(47),
  },
  currentWeatherWidgetLeftWrapper: {
    paddingTop: Theme.px(100),
    width: Theme.px(318),
  },
  currentWeatherWidgetLoadingWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  currentWeatherWidgetRightItemContent: {
    alignItems: 'baseline',
    flexDirection: 'row',
  },
  currentWeatherWidgetRightItemWrapper: {
    alignItems: 'center',
    width: Theme.px(308),
  },
  currentWeatherWidgetRightTopRow: {
    flexDirection: 'row',
    paddingBottom: Theme.px(50),
  },
  currentWeatherWidgetRightWrapper: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'flex-end',
  },
  currentWeatherWidgetFlexRowWrapper: {
    flexDirection: 'row',
  },
});
